import { useMemo, useState } from 'react';
import { FieldPath, SubmitHandler } from 'react-hook-form';
import { useHistory, useParams } from 'react-router-dom';
import { useMutation, useQueryClient, useSuspenseQuery } from '@tanstack/react-query';
import { Button } from '@givebutter/primitives/button';
import { Label } from '@givebutter/primitives/label';
import * as RadioGroup from '@givebutter/primitives/radio-group';
import { Separator } from '@givebutter/primitives/separator';
import * as SingleSelect from '@givebutter/primitives/single-select';
import { useToast } from '@givebutter/primitives/toast';
import { pluralize } from '@givebutter/util';
import {
    CreateReportScheduleParams,
    createReportSchedule,
    exportReport,
    fetchReportScheduleById,
    updateReportSchedule,
} from 'api/reports';
import { ShareReportSendToField } from 'assets/js/pages/reports/ShareReportSendToField';
import { ShareReportFormValues, WeekdayToNumberMap } from 'assets/js/pages/reports/types';
import { useFetchReportMeta } from 'assets/js/pages/reports/useFetchReportMeta';
import { getDatetimeRangeFromUpstream } from 'assets/js/pages/reports/util';
import * as Dialog from 'components/dialog';
import * as Form from 'components/form';
import { useCurrentAccount } from 'customHooks/useCurrentAccount';
import { useCurrentReport } from 'customHooks/useCurrentReport';
import { dateToServerDateTime, isoDateFromServer } from 'helpers/dates';
import { conditionallyFetchOrNull } from 'helpers/reactQuery';
import { DateTime } from 'luxon';
import { accountQueryKeys } from 'queries/account-query-keys';
import {
    ReportSchedule,
    ReportScheduleDay,
    ReportScheduleDayNumber,
    ReportScheduleStatus,
} from 'types/domain';
import { GivebutterAxiosError } from 'types/error';
import { SavedNextFilters } from 'types/request';

const WEEKDAY_NUMBER_MAP: WeekdayToNumberMap = {
    monday: 1,
    tuesday: 2,
    wednesday: 3,
    thursday: 4,
    friday: 5,
    saturday: 6,
    sunday: 7,
};

const ShareReportDialog = () => {
    const { accountId } = useCurrentAccount();
    const { data: report } = useCurrentReport();
    const { error, success } = useToast();
    const history = useHistory();
    const queryClient = useQueryClient();
    const { schedule_id } = useParams<{ schedule_id?: string }>();
    const scheduleId = schedule_id ? Number(schedule_id) : undefined;
    const { data: resourceData } = useFetchReportMeta();
    const totalResource = resourceData.meta.total;

    const { data: reportSchedule } = useSuspenseQuery({
        queryKey: accountQueryKeys.reportScheduleById(accountId, report.id, scheduleId as number),
        queryFn: () =>
            conditionallyFetchOrNull(
                () => fetchReportScheduleById(accountId, report.id, scheduleId as number),
                !!scheduleId,
            ),
    });
    const [status, setStatus] = useState(reportSchedule?.status ?? 'draft');
    const methods = Form.useForm<ShareReportFormValues>({
        defaultValues: {
            name: reportSchedule?.title ?? '',
            message: reportSchedule?.message ?? '',
            isPdfAttached: reportSchedule?.include_pdf ?? false,
            frequency: reportSchedule?.frequency === 'one_time' ? 'one-time' : 'recurring',
            scheduleDay: reportSchedule?.scheduled_day_of_week ?? 'monday',
            scheduleDayNumber: reportSchedule?.scheduled_day_of_month ?? '1',
            scheduleTime: reportSchedule?.scheduled_time
                ? DateTime.fromFormat(reportSchedule.scheduled_time, 'HH:mm:ss').toFormat('H:mm')
                : '8:00',
            scheduleInterval:
                reportSchedule && reportSchedule.frequency !== 'one_time'
                    ? reportSchedule.frequency
                    : 'weekly',
            sendDate: reportSchedule?.next_run_at ? 'future' : 'now',
            startDateTime: [
                reportSchedule?.next_run_at
                    ? isoDateFromServer(reportSchedule.next_run_at)
                    : DateTime.now().plus({ hour: 1 }).startOf('hour'),
                null,
            ],
            emails: reportSchedule?.recipients ?? [],
        },
    });
    const { control, path, handleSubmit, watch, setError, getValues, resetFormButton } = methods;
    const [interval, frequency, sendDate, scheduleDay, scheduleDayNumber, scheduleTime] = watch([
        'scheduleInterval',
        'frequency',
        'sendDate',
        'scheduleDay',
        'scheduleDayNumber',
        'scheduleTime',
    ]);

    const [start, end] = getDatetimeRangeFromUpstream(
        report.query.date_range.range as [string, string],
    );

    const { isPending, mutate: createOrUpdateReportSchedule } = useMutation({
        mutationFn: (values: ShareReportFormValues) => {
            const params = getUpstreamParams(values, report.query.filters, status);

            if (scheduleId) {
                return updateReportSchedule(accountId, report.id, scheduleId, params);
            } else {
                return createReportSchedule(accountId, report.id, params);
            }
        },
        onSuccess: () => {
            history.push(`/accounts/${accountId}/reports/${report.id}/schedules`);
            success(`Successfully ${scheduleId ? 'updated' : 'created'} report schedule!`);
            queryClient.invalidateQueries({
                queryKey: accountQueryKeys.reportSchedules(accountId, report.id),
            });
        },
        onError: (e: GivebutterAxiosError) => {
            const errors = e.response?.data.errors;
            if (errors) {
                Object.keys(errors).forEach(key => {
                    const normalizedKey = transformServerKeyIntoFormKey(
                        key as Parameters<typeof transformServerKeyIntoFormKey>[0],
                    );

                    if (normalizedKey) {
                        setError(normalizedKey as FieldPath<ShareReportFormValues>, {
                            message: errors[key][0],
                        });
                    }
                });
            } else {
                error(
                    e.response?.data?.message ??
                        `Unable to ${scheduleId ? 'update' : 'create'} report schedule`,
                );
            }
            resetFormButton();
        },
    });

    const { isPending: isSendingTestEmail, mutate: sendTestEmail } = useMutation({
        mutationFn: () => {
            const values = getValues();
            const params = getUpstreamParams(values, report.query.filters, status);

            return exportReport(accountId, report.id, { schedule: params });
        },
        onSuccess: () => {
            success('Successfully sent test email!');
        },
        onError: (e: GivebutterAxiosError) => {
            error(e.response?.data?.message ?? 'Unable to send test email');
        },
    });

    const nextRecurringDayText = useMemo(() => {
        switch (interval) {
            case 'daily':
                return getDailyRecurringNextDate(scheduleTime);
            case 'weekly':
                return getWeeklyRecurringNextDate(scheduleDay, scheduleTime);
            case 'monthly':
                return getMonthlyRecurringNextDate(scheduleDayNumber, scheduleTime);
        }
    }, [interval, scheduleDay, scheduleDayNumber, scheduleTime]);

    return (
        <Form.Provider {...methods}>
            <form
                className={'contents'}
                onSubmit={handleSubmit(
                    createOrUpdateReportSchedule as SubmitHandler<ShareReportFormValues>,
                )}
            >
                <div className={'flex h-full flex-col'}>
                    <div className={'flex flex-wrap items-center gap-8 bg-gray-50 px-8 py-6'}>
                        <div className={'flex flex-grow items-center gap-8'}>
                            <div className={'relative flex items-center justify-center'}>
                                <Dialog.Close className={'static'} />
                            </div>
                            <Dialog.Title className={'text-xl font-medium'}>
                                Share report
                            </Dialog.Title>
                        </div>
                        <div
                            className={
                                'flex w-full flex-wrap-reverse gap-4 sm:w-auto [&>*]:w-full sm:[&>*]:w-auto'
                            }
                        >
                            <Form.SubmitButton
                                noAnimation={true}
                                isSubmitting={isPending}
                                variant="transparent"
                                onClick={() => setStatus('draft')}
                            >
                                Save draft
                            </Form.SubmitButton>
                            <Button
                                disabled={isSendingTestEmail}
                                variant={'outline'}
                                onClick={() => sendTestEmail()}
                            >
                                Send test email
                            </Button>
                            <Form.SubmitButton
                                isSubmitting={isPending}
                                onClick={() => setStatus('published')}
                            >
                                {frequency === 'one-time' ? 'Send' : 'Schedule'}
                            </Form.SubmitButton>
                        </div>
                    </div>
                    <Separator className={'list-item bg-gray-200'} />
                    <div
                        className={
                            'relative flex h-auto flex-col overflow-scroll sm:h-full sm:flex-row sm:overflow-visible'
                        }
                    >
                        <div
                            className={
                                'bg-gray-25 flex h-auto flex-col items-center gap-6 overflow-visible border-r-0 px-8 pb-12 pt-12 sm:h-full sm:flex-1 sm:overflow-scroll sm:border-r sm:border-r-gray-200 sm:px-20 sm:pb-32'
                            }
                        >
                            <div className={'flex w-full max-w-screen-sm flex-col gap-6'}>
                                <Form.Container className={'gap-2'}>
                                    <p className={'text-sm text-gray-700'}>In this email</p>
                                    <p className={'font-medium text-gray-900'}>
                                        {report.name} (
                                        {`${totalResource} ${pluralize(totalResource, 'row')}`})
                                    </p>
                                    <div className={'flex flex-col gap-0.5'}>
                                        <p className={'text-sm font-medium'}>Report date range</p>
                                        <p className={'text-sm font-medium text-gray-500'}>
                                            {start.toLocaleString(DateTime.DATE_MED)} -{' '}
                                            {end.toLocaleString(DateTime.DATE_MED)}
                                        </p>
                                    </div>
                                </Form.Container>
                                <div className={'flex flex-col gap-4'}>
                                    <p
                                        className={
                                            'flex-grow text-start text-lg font-medium text-gray-900'
                                        }
                                    >
                                        Contents
                                    </p>
                                    <div className={'flex flex-col gap-4'}>
                                        <Form.Container>
                                            <Form.Label name={path('name')}>
                                                Subject
                                                <Form.LabelAsterisk />
                                            </Form.Label>
                                            <Form.TextInputRoot control={control} name={'name'}>
                                                <Form.TextInputForm />
                                            </Form.TextInputRoot>
                                            <Form.Error name={path('name')} />
                                        </Form.Container>
                                        <ShareReportSendToField />
                                        <Form.Container>
                                            <Form.Label name={path('message')}>Message</Form.Label>
                                            <Form.Textarea
                                                rows={8}
                                                control={control}
                                                name={'message'}
                                                placeholder={
                                                    'Any details you want to include with the report'
                                                }
                                            />
                                            <Form.Error name={path('message')} />
                                        </Form.Container>
                                    </div>
                                </div>
                                <div className={'flex flex-col gap-4'}>
                                    <p className={'text-lg font-medium text-gray-900'}>Schedule</p>
                                    <div className={'flex flex-col gap-4'}>
                                        <Form.Container>
                                            <Form.Label name={path('frequency')}>
                                                Frequency
                                            </Form.Label>
                                            <Form.UseFormRadioGroup
                                                control={control}
                                                name={'frequency'}
                                            >
                                                <Label
                                                    className={
                                                        'flex w-fit items-center gap-2 text-sm font-medium'
                                                    }
                                                >
                                                    <RadioGroup.Item
                                                        value={'one-time'}
                                                        className={
                                                            'radix-state-checked:bg-blue-500 flex h-4 w-4 cursor-pointer items-center justify-center rounded-full border border-gray-300'
                                                        }
                                                    >
                                                        <RadioGroup.Indicator className="h-1.5 w-1.5 rounded-full bg-white" />
                                                    </RadioGroup.Item>
                                                    One time
                                                </Label>
                                                <Label
                                                    className={
                                                        'flex w-fit items-center gap-2 text-sm font-medium'
                                                    }
                                                >
                                                    <RadioGroup.Item
                                                        value={'recurring'}
                                                        className={
                                                            'radix-state-checked:bg-blue-500 flex h-4 w-4 cursor-pointer items-center justify-center rounded-full border border-gray-300'
                                                        }
                                                    >
                                                        <RadioGroup.Indicator className="h-1.5 w-1.5 rounded-full bg-white" />
                                                    </RadioGroup.Item>
                                                    Recurring
                                                </Label>
                                            </Form.UseFormRadioGroup>
                                        </Form.Container>
                                        {frequency === 'one-time' && (
                                            <>
                                                <Form.Container>
                                                    <Form.Label name={path('frequency')}>
                                                        Send
                                                    </Form.Label>
                                                    <Form.UseFormRadioGroup
                                                        control={control}
                                                        name={'sendDate'}
                                                    >
                                                        <Label
                                                            className={
                                                                'flex w-fit items-center gap-2 text-sm font-medium'
                                                            }
                                                        >
                                                            <RadioGroup.Item
                                                                value={'now'}
                                                                className={
                                                                    'radix-state-checked:bg-blue-500 flex h-4 w-4 cursor-pointer items-center justify-center rounded-full border border-gray-300'
                                                                }
                                                            >
                                                                <RadioGroup.Indicator className="h-1.5 w-1.5 rounded-full bg-white" />
                                                            </RadioGroup.Item>
                                                            Immediately
                                                        </Label>
                                                        <Label
                                                            className={
                                                                'flex w-fit items-center gap-2 text-sm font-medium'
                                                            }
                                                        >
                                                            <RadioGroup.Item
                                                                value={'future'}
                                                                className={
                                                                    'radix-state-checked:bg-blue-500 flex h-4 w-4 cursor-pointer items-center justify-center rounded-full border border-gray-300'
                                                                }
                                                            >
                                                                <RadioGroup.Indicator className="h-1.5 w-1.5 rounded-full bg-white" />
                                                            </RadioGroup.Item>
                                                            On date
                                                        </Label>
                                                    </Form.UseFormRadioGroup>
                                                </Form.Container>
                                                {sendDate === 'future' && (
                                                    <Form.Container>
                                                        <Form.Label name={path('startDateTime')}>
                                                            Start date
                                                        </Form.Label>
                                                        <Form.UseFormDateTimePicker
                                                            control={control}
                                                            allowRange={false}
                                                            name={'startDateTime'}
                                                            minDate={DateTime.now()}
                                                        />
                                                        <Form.Error name={path('startDateTime')} />
                                                    </Form.Container>
                                                )}
                                            </>
                                        )}
                                        {frequency === 'recurring' && (
                                            <>
                                                <Form.Container>
                                                    <Form.Label name={path('scheduleInterval')}>
                                                        Schedule
                                                    </Form.Label>
                                                    <Form.SingleSelect
                                                        control={control}
                                                        name="scheduleInterval"
                                                    >
                                                        <SingleSelect.Trigger aria-label="Schedule interval" />
                                                        <SingleSelect.Content>
                                                            <SingleSelect.Item value="daily">
                                                                <SingleSelect.ItemText>
                                                                    Daily
                                                                </SingleSelect.ItemText>
                                                            </SingleSelect.Item>
                                                            <SingleSelect.Item value="weekly">
                                                                <SingleSelect.ItemText>
                                                                    Weekly
                                                                </SingleSelect.ItemText>
                                                            </SingleSelect.Item>
                                                            <SingleSelect.Item value="monthly">
                                                                <SingleSelect.ItemText>
                                                                    Monthly
                                                                </SingleSelect.ItemText>
                                                            </SingleSelect.Item>
                                                        </SingleSelect.Content>
                                                    </Form.SingleSelect>
                                                    <Form.Error name={path('scheduleInterval')} />
                                                </Form.Container>
                                                <div className={'flex gap-4 [&>*]:flex-1'}>
                                                    {interval === 'weekly' && (
                                                        <Form.Container>
                                                            <Form.Label name={path('scheduleDay')}>
                                                                Day of week
                                                            </Form.Label>
                                                            <Form.SingleSelect
                                                                control={control}
                                                                name="scheduleDay"
                                                            >
                                                                <SingleSelect.Trigger aria-label="Schedule day" />
                                                                <SingleSelect.Content>
                                                                    <SingleSelect.Item value="monday">
                                                                        <SingleSelect.ItemText>
                                                                            Monday
                                                                        </SingleSelect.ItemText>
                                                                    </SingleSelect.Item>
                                                                    <SingleSelect.Item value="tuesday">
                                                                        <SingleSelect.ItemText>
                                                                            Tuesday
                                                                        </SingleSelect.ItemText>
                                                                    </SingleSelect.Item>
                                                                    <SingleSelect.Item value="wednesday">
                                                                        <SingleSelect.ItemText>
                                                                            Wednesday
                                                                        </SingleSelect.ItemText>
                                                                    </SingleSelect.Item>
                                                                    <SingleSelect.Item value="thursday">
                                                                        <SingleSelect.ItemText>
                                                                            Thursday
                                                                        </SingleSelect.ItemText>
                                                                    </SingleSelect.Item>
                                                                    <SingleSelect.Item value="friday">
                                                                        <SingleSelect.ItemText>
                                                                            Friday
                                                                        </SingleSelect.ItemText>
                                                                    </SingleSelect.Item>
                                                                    <SingleSelect.Item value="saturday">
                                                                        <SingleSelect.ItemText>
                                                                            Saturday
                                                                        </SingleSelect.ItemText>
                                                                    </SingleSelect.Item>
                                                                    <SingleSelect.Item value="sunday">
                                                                        <SingleSelect.ItemText>
                                                                            Sunday
                                                                        </SingleSelect.ItemText>
                                                                    </SingleSelect.Item>
                                                                </SingleSelect.Content>
                                                            </Form.SingleSelect>
                                                            <Form.Error
                                                                name={path('scheduleDay')}
                                                            />
                                                        </Form.Container>
                                                    )}
                                                    {interval === 'monthly' && (
                                                        <Form.Container>
                                                            <Form.Label
                                                                name={path('scheduleDayNumber')}
                                                            >
                                                                Day of month
                                                            </Form.Label>
                                                            <Form.SingleSelect
                                                                control={control}
                                                                name="scheduleDayNumber"
                                                            >
                                                                <SingleSelect.Trigger aria-label="Schedule day number" />
                                                                <SingleSelect.Content>
                                                                    {Array.from(
                                                                        Array(27).keys(),
                                                                    ).map(dayNumber => {
                                                                        const day = dayNumber + 1;
                                                                        return (
                                                                            <SingleSelect.Item
                                                                                value={day.toString()}
                                                                                key={day}
                                                                            >
                                                                                <SingleSelect.ItemText>
                                                                                    {day}
                                                                                </SingleSelect.ItemText>
                                                                            </SingleSelect.Item>
                                                                        );
                                                                    })}

                                                                    <SingleSelect.Item value="last_day">
                                                                        <SingleSelect.ItemText>
                                                                            Last day of month
                                                                        </SingleSelect.ItemText>
                                                                    </SingleSelect.Item>
                                                                </SingleSelect.Content>
                                                            </Form.SingleSelect>
                                                            <Form.Error
                                                                name={path('scheduleDayNumber')}
                                                            />
                                                        </Form.Container>
                                                    )}
                                                    <Form.Container>
                                                        <Form.Label name={path('scheduleTime')}>
                                                            Time
                                                        </Form.Label>
                                                        <Form.UseFormTimePicker
                                                            control={control}
                                                            name={'scheduleTime'}
                                                        />
                                                        <Form.Error name={path('scheduleTime')} />
                                                    </Form.Container>
                                                </div>
                                                <div className={'flex flex-col'}>
                                                    <p className={'text-sm text-gray-600'}>
                                                        This report will start sharing on:
                                                    </p>
                                                    <p
                                                        className={
                                                            'text-sm font-medium text-gray-600'
                                                        }
                                                    >
                                                        {nextRecurringDayText}
                                                    </p>
                                                </div>
                                            </>
                                        )}
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </form>
        </Form.Provider>
    );
};

const getWeeklyRecurringNextDate = (day: ReportScheduleDay, time: string) => {
    const now = DateTime.now();
    const timeParts = DateTime.fromFormat(time, 'H:mm');
    const targetDay = WEEKDAY_NUMBER_MAP[day];

    let nextDate = now.set({ weekday: targetDay, hour: timeParts.hour, minute: timeParts.minute });
    nextDate = nextDate < now ? nextDate.plus({ weeks: 1 }) : nextDate;

    return nextDate.toLocaleString(DateTime.DATETIME_FULL);
};

const getMonthlyRecurringNextDate = (day: ReportScheduleDayNumber, time: string) => {
    const date = DateTime.fromFormat(time, 'H:mm');
    const dateWithMonthDay =
        day === 'last_day'
            ? DateTime.now().endOf('month')
            : DateTime.now().set({ day: Number(day) });
    const nextMonth = dateWithMonthDay.plus({ months: 1 });

    let computedNextDate = dateWithMonthDay < date ? nextMonth : dateWithMonthDay;
    if (date.day === dateWithMonthDay.day) {
        computedNextDate = date < dateWithMonthDay ? nextMonth : dateWithMonthDay;
    }

    return computedNextDate
        .set({ hour: date.hour, minute: date.minute })
        .toLocaleString(DateTime.DATETIME_FULL);
};

const getDailyRecurringNextDate = (time: string) => {
    const now = DateTime.now();
    const dateTime = DateTime.fromFormat(time, 'H:mm');
    const dayIncrement = now > dateTime ? 1 : 0;

    return dateTime.plus({ day: dayIncrement }).toLocaleString(DateTime.DATETIME_FULL);
};

const getUpstreamParams = (
    values: ShareReportFormValues,
    filters: SavedNextFilters,
    status: ReportScheduleStatus,
): CreateReportScheduleParams => {
    const isRecurring = values.frequency === 'recurring';
    const valueIfRecurring = <T,>(value: T) => (isRecurring ? value : undefined);
    const valueIfOneTime = <T,>(value: T) => (!isRecurring ? value : undefined);

    return {
        title: values.name,
        message: values.message,
        include_pdf: values.isPdfAttached,
        frequency: valueIfRecurring(values.scheduleInterval) ?? 'one_time',
        recipients: values.emails,
        scheduled_day_of_month:
            values.scheduleInterval === 'monthly'
                ? valueIfRecurring(values.scheduleDayNumber)
                : undefined,
        scheduled_day_of_week:
            values.scheduleInterval === 'weekly' ? valueIfRecurring(values.scheduleDay) : undefined,
        scheduled_time: valueIfRecurring(
            DateTime.fromFormat(values.scheduleTime, 'H:mm').toFormat('HH:mm'),
        ),
        start_date: valueIfOneTime(
            dateToServerDateTime(
                values.sendDate === 'now' ? DateTime.now() : values.startDateTime[0],
            ),
        ),
        filters,
        status,
    };
};

const transformServerKeyIntoFormKey = (
    serverKey: keyof Omit<
        ReportSchedule,
        'created_by' | 'filters' | 'status' | 'created_at' | 'updated_at'
    >,
): keyof ShareReportFormValues | undefined => {
    switch (serverKey) {
        case 'frequency':
            return 'scheduleInterval';
        case 'include_pdf':
            return 'isPdfAttached';
        case 'title':
            return 'name';
        case 'message':
            return 'message';
        case 'recipients':
            return 'emails';
        case 'scheduled_day_of_week':
            return 'scheduleDay';
        case 'scheduled_day_of_month':
            return 'scheduleDayNumber';
        case 'scheduled_time':
            return 'scheduleTime';
        case 'next_run_at':
            return 'startDateTime';
        default:
            return undefined;
    }
};

export { ShareReportDialog };
